/**
 * SafeImage — never renders broken image boxes client-side.
 *
 * Behaviour:
 *   - if no `src` → in `mode="client"`: collapses (renders null)
 *                  in `mode="advisor"`: renders a tasteful "Image pending"
 *                                       pill so the advisor knows to fix it
 *   - if `src` is given but fails to load: same fallback as no-src above
 *
 * The advisor pill is intentionally muted (paper bg, mono micro-caption,
 * no emoji) so it disappears into the design system.
 */
import React from "react";
import { ImageOff } from "lucide-react";

export default function SafeImage({
  src,
  alt = "",
  mode = "client", // "client" | "advisor"
  className = "",
  aspect = "aspect-[16/8]",
  placeholderLabel = "Image pending",
  testId,
}) {
  const [errored, setErrored] = React.useState(false);
  React.useEffect(() => {
    setErrored(false);
  }, [src]);

  const missing = !src || errored;

  if (missing) {
    if (mode === "client") return null;
    return (
      <div
        className={`rounded-md ${aspect} w-full bg-[hsl(var(--background-paper))]/60 border border-dashed border-[rgba(0,0,0,0.1)] flex flex-col items-center justify-center gap-1 ${className}`}
        data-testid={testId ? `${testId}-placeholder` : "safe-image-placeholder"}
      >
        <ImageOff
          className="size-4 text-[hsl(var(--foreground-muted))]"
          strokeWidth={1.5}
        />
        <p className="font-mono text-[9.5px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">
          {placeholderLabel}
        </p>
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      onError={() => setErrored(true)}
      className={`w-full ${aspect} object-cover ${className}`}
      data-testid={testId}
      loading="lazy"
    />
  );
}

export function isAdvisorPreviewMode() {
  if (typeof window === "undefined") return false;
  return (window.location.pathname || "").includes("/portal/advisor/");
}
